import {
  Flex,
  Icon,
  IconButton,
  ListItem,
  OrderedList,
  Text,
} from "@chakra-ui/react";
import _ from "lodash";
import { RiIndeterminateCircleLine } from "react-icons/ri";
import { StatusTagMetadata } from "src/components/DataDisplay/StatusTag";
import { InteractiveList } from "src/components/InteractiveList";
import {
  FORM_SCHOOL_RANK_SUB_STATUS,
  FormSchoolRankSubStatusIndex,
  FormStatusType,
} from "src/constants";
import { useGlossary } from "src/hooks/useGlossary";
import { WEGLOT_SKIP_CLASS } from "src/plugins/weglot/constants";
import * as GQL from "src/types/graphql";
import { LateEditRankedSchool, LateEditSchool } from "./types";

type Props = {
  schoolsRank: readonly LateEditRankedSchool[];
  onDeleteSchoolRank: (schoolRank: LateEditRankedSchool) => void;
  renderSelectItem: (
    item: LateEditSchool,
    subStatus?: StatusTagMetadata
  ) => React.ReactElement;
  rankingEnabled: boolean;
  onSchoolRanksUpdate: (schoolRanks: readonly LateEditRankedSchool[]) => void;
};
export const LateEditSchoolsRankList: React.FC<Props> = ({
  schoolsRank,
  onDeleteSchoolRank,
  renderSelectItem,
  rankingEnabled,
  onSchoolRanksUpdate,
}) => {
  const { glossary } = useGlossary();

  if (rankingEnabled) {
    return (
      <InteractiveList
        sortable={true}
        droppableId="RankSchools"
        items={schoolsRank}
        toId={(school) => school.school.id}
        render={(school) => {
          return renderSelectItem(school.school, getSubstatus(school));
        }}
        onSort={async (schools) => onSchoolRanksUpdate(schools)}
        helpTextId={"school-ranking-draggable-description"}
        deleteButton={{
          onClick: async (school) => {
            onDeleteSchoolRank(school);
          },
          label: glossary`Remove school`,
          hide: (school) => !canDelete(school),
        }}
        interactiveItemProps={{ className: WEGLOT_SKIP_CLASS }}
      />
    );
  }

  return (
    <OrderedList width="100%">
      {_.sortBy(schoolsRank, (schoolRank) => schoolRank.rank).map(
        (schoolRank) => {
          const hasRank = rankingEnabled && typeof schoolRank.rank === "number";

          return (
            <ListItem display="flex" key={schoolRank.school.name} pb={6}>
              <Flex
                direction="row"
                width="100%"
                alignItems="center"
                justifyContent="space-between"
              >
                <Flex alignItems="center">
                  {hasRank && (
                    <Text fontWeight={600} mr={4}>
                      {schoolRank.rank + 1}
                    </Text>
                  )}
                  <Flex direction="column" gap="2">
                    {renderSelectItem(
                      schoolRank.school,
                      getSubstatus(schoolRank)
                    )}
                  </Flex>
                </Flex>

                {canDelete(schoolRank) ? (
                  <IconButton
                    aria-label="late-delete-school-rank"
                    variant="link"
                    colorScheme="red"
                    size="sm"
                    icon={<Icon w={5} h={5} as={RiIndeterminateCircleLine} />}
                    onClick={() => onDeleteSchoolRank(schoolRank)}
                  />
                ) : null}
              </Flex>
            </ListItem>
          );
        }
      )}
    </OrderedList>
  );
};

function canDelete(schoolRank: LateEditRankedSchool): boolean {
  return schoolRank.sub_status !== GQL.offer_status_enum.Accepted;
}

function getSubstatus(
  schoolRank: LateEditRankedSchool
): FormStatusType | undefined {
  const subStatus =
    FORM_SCHOOL_RANK_SUB_STATUS[
      schoolRank.sub_status as FormSchoolRankSubStatusIndex
    ];
  const subStatusWithWaitlistPosition = subStatus
    ? {
        ...subStatus,
        longLabel: getSubstatusLabel(schoolRank) ?? subStatus.longLabel,
      }
    : undefined;

  return subStatusWithWaitlistPosition;
}

// This is for adding waitlist position to the Waitlisted substatus tag
const getSubstatusLabel = (schoolRank: LateEditRankedSchool) => {
  const waitlistPosition = schoolRank.waitlists[0]?.waitlist_position?.position;
  if (
    !schoolRank.offers[0] &&
    schoolRank.waitlists[0]?.status === GQL.waitlist_status_enum.Waitlisted &&
    waitlistPosition
  ) {
    return `${GQL.waitlist_status_enum.Waitlisted}, #${waitlistPosition}`;
  }
  return undefined;
};
